import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  EditButton,
  ShowButton,
  TopToolbar,
  SearchInput,
  DeleteWithConfirmButton,
  useListContext,
} from 'react-admin';
import { Card, Typography, Box, Chip } from '@mui/material';
import { Add as AddIcon, Security as SecurityIcon } from '@mui/icons-material';
import CustomCreateButton from '@/components/CustomCreateButton';
import DeleteWithPasswordButton from '@/components/DeleteWithPasswordButton';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '@/components/HierarchicalFilter';

const RoleListActions = () => (
  <TopToolbar>
    <CustomCreateButton
      variant='contained'
      label='Crear Rol'
      icon={<AddIcon />}
      sx={{
        height: '40px',
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
    />
  </TopToolbar>
);

const roleFilters = [
  <SearchInput
    key='search'
    source='q'
    placeholder='Buscar roles...'
    alwaysOn
    size='small'
    sx={{
      minWidth: 200,
      mb: 0.5,
    }}
  />,
];

const RolePermissionsField = ({ record }: any) => {
  if (!record?.permissions || record.permissions.length === 0) {
    return <Chip label='Sin permisos' color='default' size='small' />;
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {record.permissions
        .slice(0, 3)
        .map((permission: string, index: number) => (
          <Chip key={index} label={permission} color='primary' size='small' />
        ))}
      {record.permissions.length > 3 && (
        <Chip
          label={`+${record.permissions.length - 3} más`}
          color='default'
          size='small'
        />
      )}
    </Box>
  );
};

const RolesListContent: React.FC = () => {
  const { setFilters, filterValues } = useListContext();

  const handleFilterChange = (selections: HierarchicalFilterSelections) => {
    console.log('🔍 RolesList - Filter changed:', selections);

    // Solo actualizar si el plantId realmente cambió
    const currentPlantId = filterValues.plantId;
    const newPlantId = selections.plantId;

    // Si ambos son undefined/null, no hacer nada
    if (!currentPlantId && !newPlantId) {
      console.log('🔍 RolesList - No hay cambios en plantId, ignorando');
      return;
    }

    // Si son iguales, no hacer nada
    if (currentPlantId === newPlantId) {
      console.log('🔍 RolesList - PlantId no cambió, ignorando');
      return;
    }

    const newFilters: any = { ...filterValues };

    if (selections.plantId) {
      newFilters.plantId = selections.plantId;
    } else {
      delete newFilters.plantId;
    }

    console.log('🔍 RolesList - Setting filters:', newFilters);
    setFilters(newFilters, {});
  };

  return (
    <>
      <HierarchicalFilter
        onChange={handleFilterChange}
        levels={['plant']}
        showZone={false}
        showTable={false}
      />
      <Datagrid
        rowClick='show'
        sx={{ '& .RaDatagrid-headerCell': { fontWeight: 'bold' } }}
      >
        <TextField source='id' label='ID' />
        <TextField source='name' label='Nombre del Rol' />
        <ShowButton label='' />
        <EditButton label='' />
        <DeleteWithPasswordButton resource='roles' label='' />
      </Datagrid>
    </>
  );
};

export const RolesList: React.FC = () => (
  <Card elevation={1} sx={{ m: 2, p: 2 }}>
    <Box sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography
        variant='h5'
        component='h1'
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <SecurityIcon color='primary' />
        Gestión de Roles
      </Typography>
    </Box>
    <List
      title={false}
      actions={<RoleListActions />}
      filters={roleFilters}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      sx={{ '& .RaList-main': { mt: 0 } }}
    >
      <RolesListContent />
    </List>
  </Card>
);
